// RISKS.md/P2.39: a WAV blob played on an HTMLMediaElement, not a Web Audio oscillator. The test has
// to travel the same path the conference audio does — HTMLMediaElement.setSinkId — or it can pass
// while the real one fails. AudioContext.setSinkId is a different path and Chrome-only.
//
// Do NOT replace this with a sound file. Generating it keeps the tone free of any third-party
// licence — no sample, no attribution, no commercial-use terms — and a library chime would inherit
// all of that for a two-second beep.

// Chosen by ear from auditioned candidates: long enough to change an OS sound setting and hear the
// result without clicking again (RISKS.md/P2.39).
export const TEST_TONE_MS = 5000

const SAMPLE_RATE = 44100

// Pentatonic, so no two notes can clash however the speaker colours them. Around middle C — an
// octave up it stops sounding like a piano and starts sounding like a music box.
const NOTES = [261.63, 293.66, 392.0, 523.25]

// Spacing far under the ring-out, so every note is still sounding when the next arrives.
const NOTE_SPACING_MS = 340
const NOTE_RING_MS = 4600

// Three strings per key, a fraction of a cent apart. This is where the shimmer comes from.
const UNISON_CENTS = [0, 0.7, -0.7]

// Fewer partials and a steeper rolloff than a bright piano: the warm voicing, chosen by ear.
const HARMONICS = 8
// String stiffness pushes each partial sharp of an exact multiple. Tiny, but without it the result
// is an organ rather than a piano.
const INHARMONICITY = 0.0004
const HARMONIC_ROLLOFF = 1.55
// Bright partials die first, so the note darkens as it rings — the strongest single cue that this is
// a struck string.
const BRIGHT_DECAY = 0.55

const ATTACK_MS = 7
const TAIL_FADE_MS = 12
const PEAK = 0.82
const SILENCE = 0.0002

// One bucket per meter frame, matching SpeakerLevel's SAMPLE_MS: the bar reads this rather than
// metering the element, for the reason in the header above (RISKS.md/P2.54).
const ENVELOPE_BUCKET_MS = 50

let cached: { url: string, envelope: Float32Array } | null = null

const encodeWav = (samples: Int16Array): Blob => {
  const buffer = new ArrayBuffer(44 + samples.length * 2)
  const view = new DataView(buffer)
  const ascii = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i++) view.setUint8(offset + i, text.charCodeAt(i))
  }

  ascii(0, 'RIFF')
  view.setUint32(4, 36 + samples.length * 2, true)
  ascii(8, 'WAVEfmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true)
  view.setUint16(22, 1, true)
  view.setUint32(24, SAMPLE_RATE, true)
  view.setUint32(28, SAMPLE_RATE * 2, true)
  view.setUint16(32, 2, true)
  view.setUint16(34, 16, true)
  ascii(36, 'data')
  view.setUint32(40, samples.length * 2, true)

  for (let i = 0; i < samples.length; i++) {
    view.setInt16(44 + i * 2, samples[i], true)
  }

  return new Blob([buffer], { type: 'audio/wav' })
}

const addNote = (mixed: Float32Array, total: number, frequency: number, startFrame: number) => {
  const length = Math.min(Math.floor((NOTE_RING_MS / 1000) * SAMPLE_RATE), total - startFrame)
  if (length <= 0) return

  const attackFrames = Math.max(1, Math.floor((ATTACK_MS / 1000) * SAMPLE_RATE))
  const seconds = NOTE_RING_MS / 1000
  // Two stages: the hammer thump drops away quickly, the string sustains underneath.
  const fastRate = 9 / seconds
  const slowRate = 1.6 / seconds

  for (let harmonic = 1; harmonic <= HARMONICS; harmonic++) {
    const stretched = frequency * harmonic * Math.sqrt(1 + INHARMONICITY * harmonic * harmonic)
    if (stretched > SAMPLE_RATE / 2.2) break

    const amplitude = 1 / Math.pow(harmonic, HARMONIC_ROLLOFF) / UNISON_CENTS.length
    const decay = 1 + (harmonic - 1) * BRIGHT_DECAY
    const steps = UNISON_CENTS.map(cents =>
      (2 * Math.PI * stretched * Math.pow(2, cents / 1200)) / SAMPLE_RATE)

    for (let i = 0; i < length; i++) {
      const t = i / SAMPLE_RATE
      const envelope = 0.65 * Math.exp(-t * fastRate * decay) + 0.35 * Math.exp(-t * slowRate * decay)
      if (envelope < SILENCE) break
      const attack = i < attackFrames ? i / attackFrames : 1
      let sum = 0
      for (let s = 0; s < steps.length; s++) sum += Math.sin(steps[s] * i)
      mixed[startFrame + i] += sum * amplitude * envelope * attack
    }
  }
}

const render = (): Int16Array => {
  const total = Math.floor((TEST_TONE_MS / 1000) * SAMPLE_RATE)
  const mixed = new Float32Array(total)

  NOTES.forEach((frequency, note) => {
    addNote(mixed, total, frequency, Math.floor((note * NOTE_SPACING_MS / 1000) * SAMPLE_RATE))
  })

  // Overlapping notes and their partials sum well past full scale, so scale to the measured peak
  // rather than guessing a per-note amplitude.
  let peak = 0
  for (let i = 0; i < total; i++) peak = Math.max(peak, Math.abs(mixed[i]))
  const gain = peak > 0 ? PEAK / peak : 1

  const tailFrames = Math.floor((TAIL_FADE_MS / 1000) * SAMPLE_RATE)
  const samples = new Int16Array(total)
  for (let i = 0; i < total; i++) {
    const fade = i > total - tailFrames ? (total - i) / tailFrames : 1
    samples[i] = Math.max(-1, Math.min(1, mixed[i] * gain * fade)) * 0x7FFF
  }
  return samples
}

const buildEnvelope = (samples: Int16Array): Float32Array => {
  const perBucket = Math.floor((ENVELOPE_BUCKET_MS / 1000) * SAMPLE_RATE)
  const envelope = new Float32Array(Math.ceil(samples.length / perBucket))
  let loudest = 0
  for (let bucket = 0; bucket < envelope.length; bucket++) {
    const end = Math.min((bucket + 1) * perBucket, samples.length)
    let peak = 0
    for (let i = bucket * perBucket; i < end; i++) peak = Math.max(peak, Math.abs(samples[i]))
    envelope[bucket] = peak
    loudest = Math.max(loudest, peak)
  }
  if (loudest > 0) for (let i = 0; i < envelope.length; i++) envelope[i] /= loudest
  return envelope
}

const build = () => {
  if (!cached) {
    const samples = render()
    cached = { url: URL.createObjectURL(encodeWav(samples)), envelope: buildEnvelope(samples) }
  }
  return cached
}

export const getTestToneUrl = (): string => build().url

// 0 until built — never `build()` here, or a 20Hz poll would run the whole synthesis to draw a bar.
export const getTestToneLevel = (seconds: number): number => {
  if (!cached) return 0
  const bucket = Math.floor((seconds * 1000) / ENVELOPE_BUCKET_MS)
  return bucket >= 0 && bucket < cached.envelope.length ? cached.envelope[bucket] : 0
}
